import React, { useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import theme from '../../styles/theme';
import { evaluateGoal, formatTextContent, updateUserProgress } from '../../services/funnelService';

const MAX_GOALS = 3;
const GOAL_MAX_LENGTH = 200;

const PHASES = {
  GOAL_ENTRY: 'goal_entry',
  GOAL_FEEDBACK: 'goal_feedback',
  TIMEFRAME: 'timeframe',
};

const TIMEFRAME_STOPS = [
  '1 Month',
  '3 Months',
  '6 Months',
  '1 Year',
  '3 Years',
  '5 Years',
  '10 Years',
];
const TIMEFRAME_MONTHS = [1, 3, 6, 12, 36, 60, 120];
const TIMEFRAME_MAX = TIMEFRAME_STOPS.length - 1;
const DEFAULT_TIMEFRAME_INDEX = Math.floor(TIMEFRAME_MAX / 2);

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px);
  overflow-y: auto;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px);
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
`;

const ProgressBarContainer = styled.div`
  width: 50vw;
  max-width: 280px;
  margin: 0 auto 32px auto;
  height: 4px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.colors.doryGreen};
  border-radius: 2px;
  width: ${(props) => Math.min(100, (props.progress / props.total) * 100)}%;
  transition: width ${theme.animation.durations.fast};
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
  line-height: 1.1;
  text-wrap: balance;
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: ${theme.spacing.xl};
  text-wrap: balance;

  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
`;

const GoalBoxShell = styled.div`
  width: 100%;
  position: relative;
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing.sm};
  border: 2px solid ${(props) => {
    if (props.$locked) return 'rgba(156, 176, 189, 0.4)';
    if (props.$tone === 'challenge') return '#8b4a4a';
    if (props.$tone === 'confirm' || props.$accepted) return theme.colors.doryGreen;
    return 'rgba(64, 92, 115, 0.22)';
  }};
  background: ${(props) => {
    if (props.$locked) return 'rgba(242, 242, 242, 0.7)';
    if (props.$tone === 'challenge') return 'rgba(252, 246, 246, 0.95)';
    if (props.$tone === 'confirm' || props.$accepted) return 'rgba(242, 248, 247, 0.95)';
    return '#fff';
  }};
  opacity: ${(props) => (props.$locked ? 0.65 : 1)};
  transition:
    border-color ${theme.animation.durations.fast},
    background ${theme.animation.durations.fast};

  ${(props) =>
    !props.$locked &&
    css`
      &:hover {
        border-color: ${props.$accepted || props.$tone === 'confirm'
          ? theme.colors.doryGreen
          : theme.colors.doryBlue};
        background: ${props.$accepted || props.$tone === 'confirm'
          ? 'rgba(242, 248, 247, 1)'
          : props.$tone === 'idle'
            ? 'rgba(64, 92, 115, 0.03)'
            : undefined};
      }

      &:focus-within {
        border-color: ${props.$accepted || props.$tone === 'confirm'
          ? theme.colors.doryGreen
          : props.$tone === 'challenge'
            ? '#8b4a4a'
            : theme.colors.doryBlue};
        background: #fff;
      }
    `}
`;

const CharCount = styled.span`
  position: absolute;
  top: 8px;
  right: 12px;
  z-index: 1;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.tiny};
  font-weight: ${theme.typography.weights.medium};
  letter-spacing: ${theme.typography.letterSpacing.wide};
  color: ${theme.colors.doryLighterBlue};
  pointer-events: none;
  line-height: 1;
`;

const GoalSummaryText = styled.p`
  width: 100%;
  margin: 0;
  padding: ${theme.spacing.md};
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.medium};
  line-height: ${theme.typography.lineHeights.relaxed};
  color: ${theme.colors.text};
  text-align: left;
  text-wrap: wrap;
`;

const TimeframeGoals = styled.div`
  width: 100%;
  margin-top: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const GoalTextarea = styled.textarea`
  width: 100%;
  height: calc(${theme.typography.sizes.bodyLarge} * ${theme.typography.lineHeights.relaxed} * 2 + ${theme.spacing.md});
  min-height: calc(${theme.typography.sizes.bodyLarge} * ${theme.typography.lineHeights.relaxed} * 2 + ${theme.spacing.md});
  resize: none;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  padding-right: ${(props) => (props.$showCount ? '3.75rem' : theme.spacing.md)};
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.medium};
  line-height: ${theme.typography.lineHeights.relaxed};
  color: ${(props) => (props.$locked ? theme.colors.doryLighterBlue : theme.colors.text)};
  background: transparent;
  outline: none;
  box-shadow: none;
  overflow-y: auto;
  align-content: center;
  -webkit-appearance: none;
  appearance: none;

  &::placeholder {
    color: ${theme.colors.doryLighterBlue};
    font-weight: ${theme.typography.weights.regular};
    font-style: italic;
  }

  /* Override App.css global textarea:focus outline (causes double border with shell) */
  &&:focus,
  &&:focus-visible {
    outline: none;
    outline-offset: 0;
    box-shadow: none;
    border: none;
  }

  &:disabled {
    cursor: default;
  }
`;

const CompactButtonRow = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin-top: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;

const CompactPrimaryButton = styled.button`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.semiBold};
  min-height: 40px;
  width: auto;
  max-width: 100%;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: 0.5rem 1.1rem;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent});
  color: #fff;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const GoalSlots = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: ${theme.spacing.md};
`;

const AddGoalButton = styled.button`
  align-self: center;
  position: relative;
  box-sizing: border-box;
  width: 66px;
  height: 66px;
  margin: ${theme.spacing.sm} 0 ${theme.spacing.md} 0;
  padding: 0;
  border-radius: ${theme.borderRadius.full};
  border: 3px solid ${theme.colors.doryBlue};
  background: #fff;
  color: ${theme.colors.doryBlue};
  cursor: pointer;
  transition:
    background ${theme.animation.durations.fast},
    color ${theme.animation.durations.fast},
    border-color ${theme.animation.durations.fast};

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    background: currentColor;
    border-radius: 1px;
    transform: translate(-50%, -50%);
  }

  &::before {
    width: 42%;
    height: 3px;
  }

  &::after {
    width: 3px;
    height: 42%;
  }

  &:hover:not(:disabled) {
    background: ${theme.colors.doryBlue};
    color: #fff;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const FeedbackCard = styled.div`
  width: 100%;
  text-align: left;
  margin-top: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  background: transparent;
  border: none;
  padding: 0;

  ${CompactButtonRow} {
    margin-top: ${theme.spacing.md};
  }
`;

const FeedbackLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.semiBold};
  letter-spacing: ${theme.typography.letterSpacing.wide};
  text-transform: uppercase;
  margin-top: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.md};
  color: ${(props) =>
    props.$tone === 'confirm' ? theme.colors.doryGreen : '#8b4a4a'};
  text-wrap: balance;
`;

const FeedbackMessage = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin: 0 0 ${theme.spacing.md} 0;
  width: 100%;
  text-align: left;
  text-wrap: wrap;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  width: 100%;
  margin-top: ${theme.spacing.md};
`;

const SecondaryButton = styled.button`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.bodyLarge};
  font-weight: ${theme.typography.weights.semiBold};
  min-height: 60px;
  width: 100%;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  background: ${(props) =>
    props.$emphasized ? theme.colors.doryBlue : theme.colors.doryLighterBlue};
  color: #fff;
  cursor: pointer;
  transition:
    background ${theme.animation.durations.fast},
    transform ${theme.animation.durations.fast},
    opacity ${theme.animation.durations.fast};

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.$emphasized ? theme.colors.doryDarkerBlue : theme.colors.doryBlue};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: #8b4a4a;
  margin: 0 0 ${theme.spacing.md} 0;
`;

const GoalLoadingState = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  margin: ${theme.spacing.md} 0;
`;

const LoadingText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.doryBlue};
  font-style: italic;
  margin: 0;
  text-align: left;
`;

const LoadingIconWrap = styled.div`
  width: 56px;
  height: 56px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const pulseRing = keyframes`
  0%, 100% {
    opacity: 0.35;
    transform: scale(0.92);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

const LoadingIconRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(67, 95, 96, 0.18);
  border-top-color: ${theme.colors.doryGreen};
  animation: ${spin} 0.9s linear infinite;
`;

const LoadingIconCore = styled.svg`
  width: 28px;
  height: 28px;
  color: ${theme.colors.doryGreen};
  animation: ${pulseRing} 1.4s ease-in-out infinite;
`;

const SliderContainer = styled.div.attrs({
  className: 'slider-container',
})`
  width: 100%;
  max-width: 100%;
  margin: ${theme.spacing.xl} auto ${theme.spacing.xl} auto;
  touch-action: pan-y;
  padding: 0 ${theme.spacing.sm};
`;

const SliderTrack = styled.div.attrs({
  className: 'slider-track',
})`
  position: relative;
  height: 4px;
  background: ${theme.colors.doryLighterBlue};
  border-radius: 2px;
  margin: ${theme.spacing.xl} 0 ${theme.spacing.lg} 0;
  touch-action: none;
`;

const SliderFill = styled.div`
  height: 100%;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  border-radius: 4px;
  width: ${(props) => (props.value / props.maxValue) * 100}%;
  transition: width ${theme.animation.durations.normal};
`;

const SliderThumb = styled.div.attrs({
  className: 'slider-thumb',
})`
  position: absolute;
  top: 50%;
  left: ${(props) => (props.value / props.maxValue) * 100}%;
  transform: translate(-50%, -50%);
  width: 54px;
  height: 54px;
  background: white;
  border: 6px solid ${theme.colors.primary};
  border-radius: 50%;
  cursor: pointer;
  box-shadow: ${theme.shadows.md};
  z-index: 10;
  transition: all ${theme.animation.durations.fast};
  touch-action: none;
  user-select: none;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    background: ${theme.colors.primary};
    border-radius: 50%;
    opacity: 0.3;
  }

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: ${theme.shadows.lg};
  }

  &:active {
    transform: translate(-50%, -50%) scale(1.05);
  }

  ${(props) =>
    props.$isDragging &&
    css`
      transform: translate(-50%, -50%) scale(1.15);
      box-shadow: ${theme.shadows.xl};
      border-color: ${theme.colors.accent};
    `}

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 60px;
    height: 60px;
    border-width: 8px;

    &::after {
      width: 28px;
      height: 28px;
    }
  }
`;

const SliderLabel = styled.div`
  position: absolute;
  top: -46px;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.primary};
  color: white;
  padding: 6px 12px;
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.semiBold};
  white-space: nowrap;
  z-index: 11;
  box-shadow: ${theme.shadows.sm};

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${theme.colors.primary};
  }
`;

const SliderInput = styled.input`
  width: 100%;
  height: 4px;
  background: transparent;
  outline: none;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
  z-index: 5;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 54px;
    height: 54px;
    background: white;
    border: 6px solid ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    opacity: 1;
    box-shadow: ${theme.shadows.md};
  }

  &::-moz-range-thumb {
    width: 54px;
    height: 54px;
    background: white;
    border: 6px solid ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    box-shadow: ${theme.shadows.md};
  }
`;

const getProgress = (phase, goalCount) => {
  if (phase === PHASES.TIMEFRAME) return 5;
  return 1 + goalCount;
};

const GoalsScreen = ({ data, onResponse, userProgress = {} }) => {
  const content = data?.content || {};
  const htmlIntro = content.text
    ? formatTextContent(content.text, userProgress)
    : null;

  const [phase, setPhase] = useState(PHASES.GOAL_ENTRY);
  const [draftGoal, setDraftGoal] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [topGoals, setTopGoals] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [showDraftSlot, setShowDraftSlot] = useState(true);
  const [timeframeIndex, setTimeframeIndex] = useState(DEFAULT_TIMEFRAME_INDEX);
  const [isDragging, setIsDragging] = useState(false);
  const [timeframeTouched, setTimeframeTouched] = useState(false);
  const timeframeIndexRef = useRef(DEFAULT_TIMEFRAME_INDEX);

  const tone = feedback?.verdict || 'idle';
  const draftSlotIndex = topGoals.length;
  const timeframeLabel = TIMEFRAME_STOPS[timeframeIndex];
  const timeframeMonths = TIMEFRAME_MONTHS[timeframeIndex];

  const firstName = (() => {
    const raw =
      typeof userProgress?.userInfo?.name === 'string'
        ? userProgress.userInfo.name.trim()
        : '';
    if (!raw) return '';
    return raw.split(/\s+/)[0] || '';
  })();

  const goalTitle = firstName
    ? `What are your top goals in life right now, ${firstName}?`
    : 'What are your top goals in life right now?';
  const goalHint =
    'The best goals are clear, measurable, and meaningful to you.';
  const timeframeTitle =
    topGoals.length === 1
      ? 'What\'s the longest it might reasonably take you to achieve this goal?'
      : 'What\'s the longest it might reasonably take you to achieve all of these goals?';

  const persistGoalsProgress = async (goals, evals, draft = '', horizon = timeframeLabel) => {
    const shortcode = userProgress?.shortcode;
    const checkpointKey = data?.key;
    if (!shortcode || !checkpointKey) {
      throw new Error('Missing session or checkpoint key');
    }

    const payload = {
      checkpointKey,
      type: 'goals',
      topGoals: goals,
      evaluations: (evals || []).map((ev) => ({
        kind: ev.kind || 'top',
        goal: String(ev.goal || ''),
        verdict: ev.verdict === 'challenge' ? 'challenge' : 'confirm',
        message: String(ev.message || ''),
        acceptedAsIs: Boolean(ev.acceptedAsIs),
      })),
      timeframe: horizon || null,
      timestamp: Date.now(),
    };

    const trimmedDraft = (draft || '').trim();
    if (trimmedDraft) {
      payload.draftGoal = trimmedDraft;
    }

    // Mid-flow save: write only this checkpoint response (does not navigate).
    await updateUserProgress(shortcode, {
      [`responses.${checkpointKey}`]: payload,
    });
  };

  const handleEvaluate = async () => {
    const trimmed = draftGoal.trim();
    if (trimmed.length < 10) {
      return;
    }

    setError('');
    setIsEvaluating(true);
    setFeedback(null);

    try {
      await persistGoalsProgress(topGoals, evaluations, trimmed);
    } catch (err) {
      console.error('Failed to save goal draft:', err);
    }

    try {
      const result = await evaluateGoal({
        kind: 'top',
        goal: trimmed,
      });
      setFeedback({
        verdict: result.verdict === 'confirm' ? 'confirm' : 'challenge',
        message: result.message || '',
      });
      setPhase(PHASES.GOAL_FEEDBACK);
    } catch (err) {
      console.error('Goal evaluation failed:', err);
      setFeedback({
        verdict: 'confirm',
        message:
          "We couldn't reach the coach just now, but you can keep this goal and continue.",
      });
      setPhase(PHASES.GOAL_FEEDBACK);
    } finally {
      setIsEvaluating(false);
    }
  };

  const handleKeepGoal = async (acceptedAsIs) => {
    const trimmed = draftGoal.trim();
    if (!trimmed || !feedback) return;

    const nextEvals = [
      ...evaluations,
      {
        kind: 'top',
        goal: trimmed,
        verdict: feedback.verdict,
        message: feedback.message,
        acceptedAsIs,
      },
    ];
    const next = [...topGoals, trimmed];

    setEvaluations(nextEvals);
    setTopGoals(next);
    setDraftGoal('');
    setFeedback(null);
    setShowDraftSlot(false);
    setPhase(PHASES.GOAL_ENTRY);
    setError('');

    try {
      await persistGoalsProgress(next, nextEvals);
    } catch (err) {
      console.error('Failed to save goal:', err);
      setError('Failed to save your goal. Please try again.');
    }
  };

  const handleDraftChange = (nextValue) => {
    setDraftGoal(nextValue);
    if (feedback || phase === PHASES.GOAL_FEEDBACK) {
      setFeedback(null);
      setPhase(PHASES.GOAL_ENTRY);
    }
  };

  const handleAcceptedGoalChange = (index, nextValue) => {
    const clipped = nextValue.slice(0, GOAL_MAX_LENGTH);
    setTopGoals((prev) => prev.map((goal, i) => (i === index ? clipped : goal)));
  };

  const handleDraftKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();

    if (phase === PHASES.GOAL_FEEDBACK && feedback) {
      handleKeepGoal(feedback.verdict === 'challenge');
      return;
    }

    const canSave =
      phase === PHASES.GOAL_ENTRY &&
      !isEvaluating &&
      draftGoal.trim().length >= 10;

    if (canSave) {
      handleEvaluate();
      return;
    }

    const trimmedDraft = draftGoal.trim();
    if (!trimmedDraft && topGoals.length === 0) return;

    persistGoalsProgress(topGoals, evaluations, trimmedDraft).catch((err) => {
      console.error('Failed to save goal:', err);
      setError('Failed to save your goal. Please try again.');
    });
  };

  const handleAcceptedGoalKeyDown = (e, index) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();

    const trimmed = (topGoals[index] || '').trim();
    if (!trimmed) return;

    const nextGoals = topGoals.map((goal, i) => (i === index ? trimmed : goal));
    const nextEvals = evaluations.map((ev, i) =>
      i === index ? { ...ev, goal: trimmed } : ev,
    );

    setTopGoals(nextGoals);
    setEvaluations(nextEvals);
    e.currentTarget.blur();

    persistGoalsProgress(nextGoals, nextEvals).catch((err) => {
      console.error('Failed to save goal:', err);
      setError('Failed to save your goal. Please try again.');
    });
  };

  const handleAddGoal = () => {
    if (topGoals.length >= MAX_GOALS || showDraftSlot) return;
    setDraftGoal('');
    setFeedback(null);
    setError('');
    setShowDraftSlot(true);
    setPhase(PHASES.GOAL_ENTRY);
  };

  const snapTimeframeFromEvent = (event) => {
    let track;
    if (
      event.currentTarget?.classList?.contains('slider-track')
    ) {
      track = event.currentTarget;
    } else {
      const activeSlider = document.querySelector(
        '[data-slider-type="timeframe"]',
      );
      track = activeSlider?.querySelector('.slider-track');
    }
    if (!track || typeof track.getBoundingClientRect !== 'function') {
      return timeframeIndexRef.current;
    }

    const rect = track.getBoundingClientRect();
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const relativeX = clientX - rect.left;
    const next = Math.max(
      0,
      Math.min(
        TIMEFRAME_MAX,
        Math.round((relativeX / rect.width) * TIMEFRAME_MAX),
      ),
    );
    timeframeIndexRef.current = next;
    setTimeframeIndex(next);
    return next;
  };

  const persistTimeframeMonths = async (index = timeframeIndexRef.current) => {
    const shortcode = userProgress?.shortcode;
    const months = TIMEFRAME_MONTHS[index];
    if (!shortcode || !months) return;

    try {
      await updateUserProgress(shortcode, {
        'userInfo.goalTimeframeMonths': months,
      });
    } catch (err) {
      console.error('Failed to save goal timeframe:', err);
    }
  };

  const handleThumbMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setTimeframeTouched(true);
    setIsDragging(true);
    snapTimeframeFromEvent(event);

    const handleMouseMove = (e) => snapTimeframeFromEvent(e);
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      persistTimeframeMonths(timeframeIndexRef.current);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleThumbTouchStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setTimeframeTouched(true);
    setIsDragging(true);
    snapTimeframeFromEvent(event);

    const handleTouchMove = (e) => {
      e.preventDefault();
      snapTimeframeFromEvent(e);
    };
    const handleTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      persistTimeframeMonths(timeframeIndexRef.current);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  const goToTimeframe = () => {
    setError('');
    setPhase(PHASES.TIMEFRAME);
  };

  const finishAndSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    const responseData = {
      checkpointKey: data.key,
      type: 'goals',
      topGoals,
      evaluations,
      timeframe: timeframeLabel,
      timeframeMonths,
      timestamp: Date.now(),
    };

    try {
      await persistGoalsProgress(topGoals, evaluations, '', timeframeLabel);
      await onResponse?.(responseData);
    } catch (err) {
      console.error('Failed to save goals response:', err);
      setError('Failed to save your goals. Please try again.');
      setIsSubmitting(false);
    }
  };

  const renderFeedback = () => {
    if (!feedback?.verdict) return null;
    const isConfirm = feedback.verdict === 'confirm';
    return (
      <FeedbackCard>
        <FeedbackLabel $tone={feedback.verdict}>
          {isConfirm ? 'Looking strong' : 'Needs sharpening'}
        </FeedbackLabel>
        <FeedbackMessage>{feedback.message}</FeedbackMessage>
        <CompactButtonRow>
          <CompactPrimaryButton
            onClick={() => handleKeepGoal(feedback.verdict === 'challenge')}
            disabled={isSubmitting || !draftGoal.trim()}
          >
            {isConfirm ? 'Lock It In' : 'Keep it Anyway'}
          </CompactPrimaryButton>
        </CompactButtonRow>
      </FeedbackCard>
    );
  };

  const renderGoalSlots = () => {
    const canAddGoal =
      !showDraftSlot &&
      topGoals.length > 0 &&
      topGoals.length < MAX_GOALS &&
      phase === PHASES.GOAL_ENTRY &&
      !isEvaluating;

    return (
      <GoalSlots>
        {topGoals.map((goal, index) => (
          <div key={`accepted-goal-${index}`}>
            <GoalBoxShell $tone="confirm" $accepted>
              {goal.length > 0 && (
                <CharCount>
                  {goal.length}/{GOAL_MAX_LENGTH}
                </CharCount>
              )}
              <GoalTextarea
                $tone="confirm"
                $accepted
                $showCount={goal.length > 0}
                value={goal}
                onChange={(e) =>
                  handleAcceptedGoalChange(index, e.target.value)
                }
                onKeyDown={(e) => handleAcceptedGoalKeyDown(e, index)}
                placeholder="Describe your goal"
                maxLength={GOAL_MAX_LENGTH}
                rows={2}
                aria-label={`Goal ${index + 1}`}
              />
            </GoalBoxShell>
          </div>
        ))}

        {showDraftSlot && draftSlotIndex < MAX_GOALS && (
          <div key={`draft-goal-${draftSlotIndex}`}>
            <GoalBoxShell $tone={tone === 'idle' ? 'idle' : tone}>
              {draftGoal.length > 0 && (
                <CharCount>
                  {draftGoal.length}/{GOAL_MAX_LENGTH}
                </CharCount>
              )}
              <GoalTextarea
                $tone={tone === 'idle' ? 'idle' : tone}
                $showCount={draftGoal.length > 0}
                value={draftGoal}
                onChange={(e) => {
                  if (!isEvaluating) {
                    handleDraftChange(e.target.value.slice(0, GOAL_MAX_LENGTH));
                  }
                }}
                onKeyDown={handleDraftKeyDown}
                placeholder="Describe your goal"
                disabled={isEvaluating}
                maxLength={GOAL_MAX_LENGTH}
                rows={2}
                aria-label={`Goal ${draftSlotIndex + 1}`}
              />
            </GoalBoxShell>
            {isEvaluating && (
              <GoalLoadingState aria-live="polite" aria-busy="true">
                <LoadingText>Thinking about your goal.</LoadingText>
                <LoadingIconWrap aria-hidden="true">
                  <LoadingIconRing />
                  <LoadingIconCore
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="11"
                      cy="11"
                      r="6.5"
                      stroke="currentColor"
                      strokeWidth="1.75"
                    />
                    <path
                      d="M16.5 16.5L21 21"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                    />
                    <path
                      d="M11 8.25V13.75"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                    />
                    <path
                      d="M8.25 11H13.75"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                    />
                  </LoadingIconCore>
                </LoadingIconWrap>
              </GoalLoadingState>
            )}
            {phase === PHASES.GOAL_FEEDBACK && renderFeedback()}
            {error && <ErrorText>{error}</ErrorText>}
            {phase === PHASES.GOAL_ENTRY &&
              !isEvaluating &&
              draftGoal.trim().length >= 10 && (
              <CompactButtonRow>
                <CompactPrimaryButton onClick={handleEvaluate}>
                  Add It
                </CompactPrimaryButton>
              </CompactButtonRow>
            )}
          </div>
        )}

        {canAddGoal && (
          <AddGoalButton
            type="button"
            onClick={handleAddGoal}
            aria-label="Add another goal"
            title="Add another goal"
          />
        )}
      </GoalSlots>
    );
  };

  const renderTimeframe = () => (
    <>
      <Title style={{ marginBottom: theme.spacing.lg }}>{timeframeTitle}</Title>
      {topGoals.length > 0 && (
        <TimeframeGoals aria-label="Your goals">
          {topGoals.map((goal, index) => (
            <GoalBoxShell key={`timeframe-goal-${index}`} $tone="confirm" $accepted>
              <GoalSummaryText>{goal}</GoalSummaryText>
            </GoalBoxShell>
          ))}
        </TimeframeGoals>
      )}
      <SliderContainer data-slider-type="timeframe">
        <SliderTrack
          onClick={(e) => {
            e.preventDefault();
            setTimeframeTouched(true);
            snapTimeframeFromEvent(e);
            persistTimeframeMonths(timeframeIndexRef.current);
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            setTimeframeTouched(true);
            snapTimeframeFromEvent(e);
            persistTimeframeMonths(timeframeIndexRef.current);
          }}
        >
          <SliderFill value={timeframeIndex} maxValue={TIMEFRAME_MAX} />
          <SliderThumb
            value={timeframeIndex}
            maxValue={TIMEFRAME_MAX}
            $isDragging={isDragging}
            onMouseDown={handleThumbMouseDown}
            onTouchStart={handleThumbTouchStart}
          >
            <SliderLabel>{timeframeLabel}</SliderLabel>
          </SliderThumb>
          <SliderInput
            type="range"
            min={0}
            max={TIMEFRAME_MAX}
            step={1}
            value={timeframeIndex}
            onChange={(e) => setTimeframeIndex(parseInt(e.target.value, 10))}
            aria-label={timeframeTitle}
          />
        </SliderTrack>
      </SliderContainer>
      {error && <ErrorText>{error}</ErrorText>}
      <ButtonGroup>
        <SecondaryButton
          $emphasized={timeframeTouched}
          onClick={finishAndSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving…' : content.ctaText || 'Continue'}
        </SecondaryButton>
      </ButtonGroup>
    </>
  );

  const renderPhase = () => {
    if (phase === PHASES.TIMEFRAME) {
      return renderTimeframe();
    }

    return (
      <>
        {htmlIntro && phase === PHASES.GOAL_ENTRY && topGoals.length === 0 && (
          <Description dangerouslySetInnerHTML={{ __html: htmlIntro }} />
        )}
        <Title>{goalTitle}</Title>
        <Description>{goalHint}</Description>
        {renderGoalSlots()}
        {phase === PHASES.GOAL_ENTRY && (
          <ButtonGroup>
            {error && <ErrorText>{error}</ErrorText>}
            <SecondaryButton
              $emphasized={topGoals.length > 0}
              onClick={goToTimeframe}
              disabled={
                topGoals.length === 0 || isEvaluating || isSubmitting
              }
            >
              {topGoals.length <= 1
                ? 'Continue With This Goal'
                : 'Continue With These Goals'}
            </SecondaryButton>
          </ButtonGroup>
        )}
      </>
    );
  };

  return (
    <Container>
      <Content>
        <ProgressBarContainer>
          <ProgressFill
            progress={getProgress(phase, topGoals.length)}
            total={5}
          />
        </ProgressBarContainer>
        {renderPhase()}
      </Content>
    </Container>
  );
};

export default GoalsScreen;
